import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { User } from 'anchor-shared/lib/model/entity/user';
import { UserDAO } from '../../../../../core/services/dao/userDAO';
import { RoleDAO } from '../../../../../core/services/dao/roleDAO';
import { Role } from 'anchor-shared/lib/model/entity/role';
import { ErrorDialogService } from '../../../../shared/errorDialog/errorDialogService';
import { CaptainDAO } from '../../../../../core/services/dao/captainDAO';
import { BoatOwner } from 'anchor-shared/lib/model/entity/boatOwner';
import { BoatOwnerDAO } from '../../../../../core/services/dao/boatOwnerDAO';
import { NgForm } from '@angular/forms';
import * as moment from 'moment';
import { NumberFormatterService } from '../../../../../core/services/number.formatter.service';
import { ConstantMan } from '../../../../../core/services/constantMan';
import { isCustomer } from '../../../../login/login.component';
import { CrewMemberDAO } from '../../../../../core/services/dao/crewMemberDAO';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { unsubscribeFromAll } from '../../../../../core/pure-functions/rx-helper';
import { CrewMember } from 'anchor-shared/lib/model/entity/crewMember';
import { ContainsSubscriptions } from '../../../../../core/services/interfaces/contains-subscriptions';
import { Observable } from 'rxjs/internal/Observable';
import * as intlTelInput from 'intl-tel-input';
import { PayoutDAO } from '../../../../../core/services/dao/payoutDAO';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CustomerDAO } from '../../../../../core/services/dao/customerDAO';
import { take } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { CharterDAO } from '../../../../../core/services/dao/charterDAO';
import { Charter } from 'anchor-shared/lib/model/entity/charter';
import { ListGridAsyncData } from '../../../../shared/listGrid/listGrid.component';

interface NameCheckReturn {
  emailDuplicate: boolean;
}

@Component({
  selector: 'app-edit-user',
  templateUrl: './edit-user.component.html',
  styleUrls: ['./edit-user.component.scss'],
})
export class EditUserComponent
  implements OnInit, OnDestroy, ContainsSubscriptions, AfterViewInit
{
  @ViewChild('intlTelInput') private _inputElement!: ElementRef;
  private _intlTelInput: any;
  public E164PhoneNumber!: string | null;
  public subs: Subscription[] = [];
  public model: User;
  public boatOwner: BoatOwner;
  public selectedId: any;
  public userRoles: number[] = [];
  public status: boolean;
  public newPassword: string;
  public confirmPassword: string;
  public roles: Role[];
  public rolesForDisplay: Role[] = [];
  public emailTaken;
  public birthdate: string;
  public userIs18 = true;
  @ViewChild('editUserForm') userForm: NgForm;
  public crew: CrewMember;
  isAffiliate = false;
  public stripeEmailSent: boolean = false;
  public stripeUserUrl: string;
  public charters;
  public columnDefs;
  public editPath;
  public listGridAsyncData = new BehaviorSubject<ListGridAsyncData>({
    count: 0,
    page: 0,
    rows: [],
    searchQuery: null,
    numberOfPages: null,
    sortBy: null,
    sortOrder: null,
  });
  public limit: number = 10;
  public loadingCharters: boolean = true;
  public listGridAsyncValidator;

  public dateMask = ConstantMan.PATTERNS.DATE;
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private userDAO: UserDAO,
    private roleDAO: RoleDAO,
    private captainDAO: CaptainDAO,
    private crewMemberDAO: CrewMemberDAO,
    private boatOwnerDAO: BoatOwnerDAO,
    private customerDAO: CustomerDAO,
    private payoutDao: PayoutDAO,
    public charterDAO: CharterDAO,
    private dialog: ErrorDialogService,
    private numFormatter: NumberFormatterService,
    private snackBar: MatSnackBar,
  ) {
    this.listGridAsyncValidator = { runSearch: this.runSearch.bind(this) };
  }

  ngAfterViewInit(): void {
    const intlTelInputInstance = intlTelInput;
    setTimeout(() => {
      this._intlTelInput = intlTelInputInstance(
        this._inputElement.nativeElement,
        { preferredCountries: ['us', 'mx'] },
      );
    }, 1000);
  }

  get intlTelInput(): any {
    return this._intlTelInput;
  }

  get internationalPhoneNumber(): string {
    return this.model.phoneNumber;
  }

  set internationalPhoneNumber(value: string) {
    if (!!value) {
      this._intlTelInput.setNumber(value);
    }
    this.model.phoneNumber = value;
    this.i18nizePhoneNumber();
  }

  public i18nizePhoneNumber(): void {
    this.E164PhoneNumber = null;
    if (this._intlTelInput.isValidNumber()) {
      this.E164PhoneNumber = this._intlTelInput.getNumber();
    }
  }

  hasInvalidPhoneNumber(): boolean {
    if (this._intlTelInput && this._intlTelInput.isValidNumber()) {
      return false;
    }
    return true;
  }

  ngOnInit() {
    this.route.params.subscribe((params) => {
      this.selectedId = +params['id'];
      this.initModel();
      this.initRoles();
    });
    this.emailTaken = false;

    this.columnDefs = [
      {
        headerName: 'Charter Id',
        field: 'id',
        filter: 'number',
        maxWidth: 75,
      },
      {
        headerName: 'Date',
        field: 'date',
        filter: 'date',
        minWidth: 100,
      },
      {
        headerName: 'Duration',
        field: 'duration',
        filter: 'number',
        valueFormatter: (val) => val.data.duration + ' hours',
        minWidth: 100,
      },
      {
        headerName: 'Boat',
        field: 'boatName',
        filter: 'string',
        minWidth: 100,
      },
      {
        headerName: 'Status',
        valueGetter: (field) => this.getTypeofCharter(field.data),
        filter: 'string',
        minWidth: 100,
      },
    ];
    this.editPath = 'manage/manage-charter';
  }

  onSubmit() {
    this.model.status = this.status ? 'active' : 'inactive';
    this.model.phoneNumber = this.E164PhoneNumber;
    this.model.username = this.model.email;
    this.userRoles = this.model.roles.map((role) => role.id);
    this.model.birthday = this.numFormatter.getDateFromDateString(
      this.birthdate,
    );
    this.userDAO.adminUpdateUser(this.model, this.userRoles).subscribe(
      (data) => {
        this.router.navigate(['manage/admin/users']).catch((err) => {
          console.error(err);
        });
      },
      (error) => {
        console.error(error);
      },
    );
  }

  checkAge(evt) {
    this.userIs18 = this.numFormatter.isDate18orMoreYearsOld(evt.target.value);
    if (!this.userIs18) {
      this.userForm.form.controls.birthday.setErrors({ valid: this.userIs18 });
    }
  }

  cancel() {
    this.router.navigate(['manage/admin/users']);
  }

  emailBlurred(emailString: string) {
    this.userDAO
      .checkEmail<NameCheckReturn>(emailString, this.model.id)
      .subscribe((res) => {
        this.emailTaken = res.emailDuplicate;
      });
  }

  goToCaptain() {
    this.router.navigate(['manage/admin/captain/edit/' + this.model.id]);
  }

  resetPassword() {
    if (this.newPassword === this.confirmPassword) {
      let updateUser = this.userDAO.updateAndLogin(
        this.model.id,
        null,
        this.newPassword,
      );
      updateUser.subscribe(
        (res) => {
          this.dialog.open('Success', 'Password has been updated');
        },
        (err) => {
          console.log(err);
          this.dialog.open('Fail', 'Password update failed');
        },
      );
    }
  }

  makeCaptain() {
    this.captainDAO.addNewCaptainToExistingUser(this.model.id).subscribe(
      (captain) => {
        this.router.navigate(['manage/admin/captain/edit/' + this.model.id]);
      },
      (err) => {
        console.error(err);
      },
    );
  }

  makeCrew() {
    const crewMemberSub = this.crewMemberDAO
      .addCrewMemberToUser({ userId: this.model.id })
      .subscribe((newCrew) => {
        this.crew = newCrew;
        let ifNotCrewRole = true;
        let crewRole: Role[] = this.roles.filter(
          (role) => role.type === 'Crew',
        );
        this.model.roles.map((role) => {
          if (role.type === 'Crew') {
            ifNotCrewRole = false;
          }
        });
        if (ifNotCrewRole && crewRole[0]) {
          this.model.roles.push(crewRole[0]);
        }
      });
    this.subs.push(crewMemberSub);
  }

  removeCrew() {
    if (this.crew && this.crew.id) {
      this.crewMemberDAO.deleteById(this.crew.id).subscribe((crew) => {
        this.crew = null;
        let newRoles: Role[] = [];
        this.model.roles.map((role) => {
          if (role.type !== 'Crew') {
            newRoles.push(role);
          }
        });
        this.model.roles = newRoles;
      });
    }
  }

  makeBoatOwner() {
    this.boatOwnerDAO.addNewBoatOwnerToExistingUser(this.model.id).subscribe(
      (boatOwner) => {
        this.model.boatOwner = boatOwner;
        this.snackBar.open('User successfully set as boat owner.', null, {
          duration: 4000,
          verticalPosition: 'top',
        });
      },
      (err) => {
        console.error(err);
        this.snackBar.open(
          'Something went wrong trying to set user as boat owner.',
          null,
          {
            duration: 4000,
            verticalPosition: 'top',
          },
        );
      },
    );
  }

  isCustomer(): boolean {
    return isCustomer(this.model);
  }

  ngOnDestroy(): void {
    unsubscribeFromAll(this.subs);
  }

  private initModel() {
    const userSub = this.userDAO
      .findUserWithDetailsById(this.selectedId)
      .subscribe((user) => {
        this.model = user;
        this.initAsyncData();
        this.status = this.model.status === 'active';
        if (user.birthday) {
          this.birthdate = moment(user.birthday).format('MM/DD/YYYY');
        }
        if (user.email) {
          this.model.email = user.email;
        }
        if (environment.production) {
          this.stripeUserUrl = `https://dashboard.stripe.com/connect/accounts/${this.model.userPaymentDetails.stripeUserId}/activity`;
        } else {
          this.stripeUserUrl = `https://dashboard.stripe.com/test/connect/accounts/${this.model.userPaymentDetails.stripeUserId}/activity`;
        }
      });
    this.subs.push(userSub);
  }

  private initRoles() {
    // Get all roles to display in role selection dropdown
    const getAllRoles = this.roleDAO.getRolesWithPermissions();
    // Get roles user currently has
    const getUserRoles = this.roleDAO.getRolesForUser(this.selectedId);
    const getCrew = this.crewMemberDAO.findCrewByUserId(this.selectedId);

    const roleObservables: Observable<any>[] = [
      getAllRoles,
      getUserRoles,
      getCrew,
    ];

    const combineSub = combineLatest(roleObservables).subscribe(
      (roleValues) => {
        this.roles = roleValues[0];
        this.model.roles = roleValues[1];
        this.crew = roleValues[2];
        this.checkAffiliateRole(this.model.roles);

        this.roles.forEach((role) => {
          if (role.type !== 'Crew' && role.type !== 'Customer') {
            this.rolesForDisplay.push(role);
          }
        });
      },
    );

    this.subs.push(combineSub);
  }

  private initAsyncData() {
    const { page, sortOrder, sortBy } = this.listGridAsyncData.value;

    const asyncDataSub = this.charterDAO
      .getCustomerCharterGrid(
        this.model.id,
        page,
        sortBy,
        sortOrder,
        this.limit,
      )
      .subscribe((chartersAndCount) => {
        this.listGridAsyncData.next({
          rows: chartersAndCount.rows,
          count: chartersAndCount.count,
          page: this.listGridAsyncData.value.page,
          searchQuery: this.listGridAsyncData.value.searchQuery,
          numberOfPages: Math.floor(chartersAndCount.count / this.limit),
          sortOrder: this.listGridAsyncData.value.sortOrder,
          sortBy: this.listGridAsyncData.value.sortBy,
        });

        this.loadingCharters = false;
      });
    this.subs.push(asyncDataSub);
  }

  runSearch(page, query, sortBy, sortOrder) {
    return this.charterDAO.getCustomerCharterGrid(
      this.model.id,
      page,
      sortBy,
      sortOrder,
      this.limit,
    );
  }

  pageChange($event: number) {
    this.listGridAsyncData.value.page = $event;
    this.initAsyncData();
  }

  requestStripeSignup($event, user: User) {
    $event.preventDefault();
    this.stripeEmailSent = true;
    const customerName = `${user.firstName} ${user.lastName}`;
    const payoutAmount = `$0.00`;

    const stripeSignupSub = this.payoutDao
      .sendRequestStripeSignUpEmail(customerName, payoutAmount, user.id)
      .subscribe((emailSendSuccess) => {
        this.snackBar.open('The request email was successfully sent.', null, {
          duration: 4000,
          verticalPosition: 'top',
        });
      });
    this.subs.push(stripeSignupSub);
  }

  addCustomerRole() {
    this.customerDAO
      .getForUserByUserId(this.model.id)
      .pipe(take(1))
      .subscribe((customer) => {
        if (customer) {
          this.selectCustomerRole();
        } else {
          this.customerDAO
            .adminCreateCustomer(this.model.id)
            .pipe(take(1))
            .subscribe((customer) => {
              this.selectCustomerRole();
            });
        }
      });
  }

  selectCustomerRole() {
    let customerRole: Role[] = this.roles.filter(
      (role) => role.type === 'Customer',
    );
    if (customerRole[0]) {
      this.model.roles.push(customerRole[0]);
    }
  }

  private checkAffiliateRole(roles: Role[]): void {
    if (roles) {
      roles.forEach((role) => {
        if (role.type === 'Affiliate') {
          this.isAffiliate = true;
        }
      });
    }
  }

  isBeforeCharterMoment(charter: Charter): boolean {
    const now = moment();
    const charterDateTime = moment(
      charter.date + ' ' + charter.charterEndTime,
      'YYYY-MM-DD hh:mm:a',
    );
    return now.isBefore(charterDateTime);
  }

  getTypeofCharter(charter) {
    if (!charter.cancelled) {
      if (!charter.completed) {
        if (
          !['Available', 'Pending customer response'].includes(
            charter.availabilityStatus,
          ) &&
          !charter.paymentReceived &&
          !charter.offers?.length &&
          this.isBeforeCharterMoment(charter)
        ) {
          return 'approved';
        } else if (
          !charter.paymentReceived &&
          this.isBeforeCharterMoment(charter)
        ) {
          return 'pending';
        } else if (
          charter.paymentReceived &&
          this.isBeforeCharterMoment(charter)
        ) {
          return 'upcoming';
        } else if (
          charter.paymentReceived &&
          !this.isBeforeCharterMoment(charter)
        ) {
          return 'completed';
        } else {
          return 'cancelled';
        }
      } else {
        return 'completed';
      }
    } else {
      return 'cancelled';
    }
  }
}
